/**
 * /pipeline-facility/jobs/:id/digs/new — plan a dig on a job
 * (POST /pipeline-facility/jobs/:id/digs, fieldiq-api#1219).
 *
 * The anomalies offered are the job site's that are not yet on a dig
 * (`undug=true`); each one picked becomes a feature on the new dig. The
 * chainage defaults to nothing — the API takes the first picked anomaly's when
 * none is typed. A refusal (an anomaly from another site, a closed job) is said
 * inline and nothing is created.
 */
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../../components/layout/PageHeaderBand';
import { usePfJob } from '../../../hooks/usePipelineFacility';
import { useCreatePfDig, usePfAnomalies } from '../../../hooks/usePfDigs';
import { blankToNull, fmtNumber } from '../../../lib/pipeline-facility';
import { numOrNull, pfDigRefusal } from '../../../lib/pf-digs';
import type { PfDigCreate } from '../../../types/pf-digs';
import { useBackToList } from '../../downhole/recordPage';
import { PF_DATE_INPUT, PF_EYEBROW, PF_INPUT, PF_PRIMARY, PF_SECONDARY, PfEmpty, PfField, PfFormError, PfLoading, PfPanel } from '../PfBits';
import { PfFormGrid, unitLabel } from './DigBits';

export default function PfNewDigScreen() {
  const { id: jobId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const back = useBackToList(`/pipeline-facility/jobs/${jobId}?tab=digs`);
  const jobQuery = usePfJob(jobId);
  const job = jobQuery.data?.data ?? null;
  const u = jobQuery.data?.meta.units ?? null;
  const anomaliesQuery = usePfAnomalies({ siteId: job?.siteId, undug: 'true' }, !!job?.siteId);
  const anomalies = anomaliesQuery.data?.data ?? [];
  const create = useCreatePfDig();
  const [picked, setPicked] = useState<string[]>([]);
  const [chainage, setChainage] = useState('');
  const [ticket, setTicket] = useState('');
  const [validTo, setValidTo] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const toggle = (id: string) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!jobId) return;
    setError(null);
    const c = numOrNull(chainage);
    if (c !== null && !Number.isFinite(c)) return setError('Chainage must be a number.');
    const body: PfDigCreate = {};
    if (picked.length) body.anomaly_ids = picked;
    if (c !== null) body.chainage = c;
    if (blankToNull(ticket)) body.one_call_ticket = ticket.trim();
    if (validTo) body.one_call_valid_to = validTo;
    if (blankToNull(notes)) body.notes = notes.trim();
    create.mutate(
      { jobId, body },
      {
        onSuccess: (r) => {
          toast.success(`${r.data?.digNumber ?? 'Dig'} planned`);
          navigate(r.data ? `/pipeline-facility/digs/${r.data.id}` : `/pipeline-facility/jobs/${jobId}?tab=digs`, { replace: true });
        },
        onError: (err) => setError(pfDigRefusal(err, 'The dig could not be planned.')),
      },
    );
  };

  return (
    <div data-testid="pf-dig-new-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={PF_EYEBROW}
        title="New dig"
        description={job ? `${job.jobNumber} · ${job.siteName ?? '—'}` : undefined}
        onBack={back}
        backLabel="Back"
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px]">
          {jobQuery.isLoading ? (
            <PfLoading label="Loading job" />
          ) : !job ? (
            <PfPanel eyebrow="Dig" title="New dig">
              <PfEmpty>This job was not found.</PfEmpty>
            </PfPanel>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <PfPanel eyebrow="Anomalies on this site, not yet dug" title="What is being dug" testId="pf-dig-new-anomalies">
                {anomaliesQuery.isLoading ? (
                  <PfLoading label="Loading anomalies" />
                ) : anomalies.length === 0 ? (
                  <PfEmpty>No undug anomalies on {job.siteName ?? 'this site'}. Enter the dig chainage below, or import the ILI list first.</PfEmpty>
                ) : (
                  <ul className="divide-y divide-fieldiq-border">
                    {anomalies.map((a) => (
                      <li key={a.id}>
                        <label className="flex cursor-pointer items-center gap-3 px-4 py-3">
                          <input type="checkbox" className="size-5 accent-fi-amber" checked={picked.includes(a.id)} onChange={() => toggle(a.id)} aria-label={`Dig ${a.featureId}`} />
                          <span className="font-mono font-bold text-white">{a.featureId}</span>
                          <span className="text-sm text-fieldiq-text">{a.featureTypeLabel}</span>
                          <span className="ml-auto font-mono text-sm text-fieldiq-text">
                            {fmtNumber(a.chainage)}
                            {u?.chainage ? ` ${u.chainage}` : ''} · {a.depthPctWt == null ? '—' : `${fmtNumber(a.depthPctWt)}%`} · {a.clockPosition || '—'}
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
              </PfPanel>
              <PfPanel eyebrow="Dig" title="Location and permits">
                <div className="space-y-4 px-4 py-4">
                  <PfFormError message={error} />
                  <PfFormGrid>
                    <PfField label={unitLabel('Chainage', u?.chainage)} hint="Blank uses the first picked anomaly's">
                      <input className={PF_INPUT} inputMode="decimal" aria-label="Chainage" value={chainage} onChange={(e) => setChainage(e.target.value)} />
                    </PfField>
                    <PfField label="One-call / ground disturbance ticket">
                      <input className={PF_INPUT} aria-label="One-call ticket" value={ticket} onChange={(e) => setTicket(e.target.value)} />
                    </PfField>
                    <PfField label="Ticket valid to">
                      <input type="date" className={PF_DATE_INPUT} aria-label="Ticket valid to" value={validTo} onChange={(e) => setValidTo(e.target.value)} />
                    </PfField>
                  </PfFormGrid>
                  <PfField label="Notes">
                    <textarea className={`${PF_INPUT} h-20 py-2`} aria-label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
                  </PfField>
                  <div className="flex justify-end gap-2">
                    <button type="button" className={PF_SECONDARY} onClick={back} disabled={create.isPending}>
                      Cancel
                    </button>
                    <button type="submit" className={PF_PRIMARY} disabled={create.isPending}>
                      {create.isPending ? 'Planning…' : 'Plan dig'}
                    </button>
                  </div>
                </div>
              </PfPanel>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
